import { createSelector, createSlice } from "@reduxjs/toolkit";
import { storageApi } from "./api";
import { sliceNames } from "../util/constants";
import { selectAuthenticatedUserRoles } from "../authorization/slices";
import { ROLES, RTK_STATUSES } from "../../util/constants";
import { createAdapter, getAdapter } from "./adapters";
import { generateParametersFromDownloadJSONQueryFromS3Url, s3Prefix } from "../../util/functions";
import normalizer from "../util/entityAdapters/normalizers/formInputs";

const initialState = {
  referenceKeys: {},
  status: RTK_STATUSES.uninitialized,
  downloadJsonDataReferenceMap: {},
  preview: {}
};

const _initializeAdapterForPath = (state, action) => {
  const { formType, s3Path, formId } = action.payload;

  if(s3Path) {
    const adapter = getAdapter(s3Path) || createAdapter(s3Path);
    if (!state[s3Path]) {
      state[s3Path] = adapter.getInitialState();
    }
    if(formType) {
      _setReferenceKey(state, {
        payload: {
          key: formType,
          value: s3Path
        }
      })
    }
    if(formId) {
      _setReferenceKey(state, {
        payload: {
          key: formId,
          value: s3Path
        }
      })
    }
    return adapter
  }
};

const _setReferenceKey = (state, action) => {
  const { key, value } = action.payload || {}
  if(key && value) {
    state.referenceKeys[key] = value
  }
}

const _appendQuestions = (state, action) => {
  const { questions, s3Path, formId, formType } = action.payload;
  if(s3Path) {
    const adapter = _initializeAdapterForPath(state, {
      payload: {
        s3Path,
        formId,
        formType
      }
    });
    
    if (adapter) {
      adapter.upsertMany(state[s3Path], questions);
    }
  }
}

const recreateS3Link = ({ path, s3Path, options }) => {
  if(s3Path?.startsWith("s3://")) {
    return s3Path
  }
  if(path?.startsWith("s3://")) {
    return path
  }
  const bucket = options?.bucket || ''
  const objPath = path.startsWith("/") ? path : `/${path}`
  if(bucket && s3Prefix && objPath) {
    return `${s3Prefix}${bucket}${objPath}`
  }
  return path || ''
}

export const questionsSlice = createSlice({
  name: sliceNames.questions,
  initialState,
  reducers: {
    initializeAdapter: (state, action) => {
      _initializeAdapterForPath(state, action);
    },
    setReferenceKey: _setReferenceKey,
    appendQuestions: _appendQuestions
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      storageApi.endpoints.downloadJsonData.matchPending,
      (state, result) => {
        const s3Path = recreateS3Link(result?.meta?.arg?.originalArgs) 
        const existingAdapter = getAdapter(s3Path)
        if(s3Path && !existingAdapter) {
          _initializeAdapterForPath(state, {
            payload: {
              s3Path
            }
          })
        }
      }
    );
    builder.addMatcher(
      storageApi.endpoints.downloadJsonData.matchFulfilled,
      (state, result) => {
        const questions = result?.payload?.questions || []
        const s3Path = recreateS3Link(result?.meta?.arg?.originalArgs)
        if(s3Path && questions?.length) {
          _appendQuestions(state, {
            payload: {
              questions, 
              s3Path
            }
          })
        }
      }
    );
  }
});

export const { initializeAdapter, appendQuestions, setReferenceKey } = questionsSlice.actions;

export const selectPathByFormID = (state, formId) => {
  const s3Path = state?.[sliceNames?.questions]?.referenceKeys?.[formId];
  return s3Path;
};

export const parsePathFromParameters = (state, { identifier, path: _path, type }) => {
  if(_path) {
    return _path
  }
  const pathFromIdentifier = identifier ? selectPathByFormID(state, identifier) : ""
  const pathFromType = type ? selectS3PathFromType(state, type) : ""
  
  return pathFromIdentifier || pathFromType
};

export const getSelectorByType = (state, { identifier, path: _path, type }) => {
  const path = parsePathFromParameters(state, { identifier, path: _path, type })
  return getAdapter(path)
};

export const getSelectorsFromAdapterForType = (state, { identifier, path: _path, type }) => {
  const path = parsePathFromParameters(state, { identifier, path: _path, type })

  if(!path) {
    return {
      selectors: undefined,
      sliceOfState: undefined
    }
  }
  const selector = getSelectorByType(state, { path })
  const relevantState = state?.[sliceNames?.questions]?.[path]
  const selectors = selector?.getSelectors((currentState) => currentState?.[sliceNames?.questions]?.[path])

  return {
    selectors,
    sliceOfState: relevantState,
  }
};

export const selectByQuestionId = (state, options = {}) => {
  const { questionId, ...rest } = options || {} 
  const { selectors } = getSelectorsFromAdapterForType(state, rest)
  if(selectors?.selectById) {
    return selectors.selectById(state, questionId) || {}
  }
  return {}
}

export const selectQuestionIDs = (state, options = {}) => {
  const { selectors } = getSelectorsFromAdapterForType(state, options)
  let identifiers = []
  if(selectors?.selectIds) {
    const qids = selectors.selectIds(state)
    if(qids?.length) {
      identifiers = qids
    }
  }
  return identifiers
}
 

export const selectAllQuestionsByFormType = (state, { formType, formId }) => {
  const path = selectS3PathFromType(state, formType);
  const { selectors } = getSelectorsFromAdapterForType(state, {
    identifier: formId,
    type: formType,
  });
  if (selectors?.selectAll) {
    const all = selectors.selectAll(state);
    if (all?.length) {
      return all;
    }
  }
  const downloadJsonDataParams =
    generateParametersFromDownloadJSONQueryFromS3Url(path);
  const { data } = storageApi.endpoints.downloadJsonData.select(
    downloadJsonDataParams
  )(state);
  if (Array.isArray(data?.questions) && data.questions.length) {
    const normalized = data.questions.map(normalizer).filter(Boolean)
    return normalized
  }

  return [];
};

export const selectUserIsGMSAuthorized = (state) => {
  const roles = selectAuthenticatedUserRoles(state);
  const isInGMS = Object.values(ROLES).some((role) => roles.includes(role));

  return isInGMS;
};

export const selectAllowedToDownloadFromNonPublicS3 = (state) => {
  return selectUserIsGMSAuthorized(state);
};

export const selectReferenceKeys = (state) => {
  return state?.[sliceNames.questions]?.referenceKeys;
};

export const selectS3PathFromType = (state, type) => {
  const referenceKeys = selectReferenceKeys(state)
  return referenceKeys?.[type];
};

export const selectStateForS3Path = (state, path) => {
  return state?.[sliceNames?.questions]?.[path];
};

export const parseS3UrlFromOptions = (state, options = {}) => {
  const s3Url =
    options?.s3Url ||
    selectS3PathFromType(state, options?.formId) ||
    selectS3PathFromType(state, options?.formType);
    
  const params = generateParametersFromDownloadJSONQueryFromS3Url(s3Url);
  if(s3Url?.startsWith(s3Prefix)) {
    params.s3Path = s3Url;
  }
  return params;
};

export const selectFormInfoForS3Url = (state, options = {}) => {
  const downloadJsonDataParams = parseS3UrlFromOptions(state, options)
  const response = storageApi.endpoints.downloadJsonData.select(downloadJsonDataParams)(
    state
  );
  return response
};

export const selectFormHeadingInfo = createSelector(
  [(state, options) => selectFormInfoForS3Url(state, options)?.data],
  (data) => ({
    description: data?.description || "",
    heading: data?.heading || "",
    subheading: data?.subheading || ""
  })
)

export const selectStatusForQuestionsAdapters = (state) => {
    return state?.[sliceNames?.questions]?.status
}
